import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';

import { CustomerIssue } from '../../model';
import CustomerIssueDetails from '../components/CustomerIssueDetails';
import CustomerIssueUpdateForm from '../components/CustomerIssueUpdateForm';
import axios from '../../../sharedModules/utils/axios.utils';

const CustomerIssueDetailPage: React.FunctionComponent = () => {
  const { recno } = useParams<{ recno: string }>();
  const history = useHistory();

  const [issue, setIssue] = useState<CustomerIssue | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const fetchIssue = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get<string, any>('/customer_issues/servman_customer_issues', {
        params: {
          'ransack[recno_eq]': recno,
        },
      });

      const issues = response?.data?.result || [];
      if (issues.length > 0) {
        setIssue(issues[0]);
      } else {
        setError('Customer issue not found');
      }
    } catch (err: any) {
      console.error('Error fetching customer issue:', err);
      setError('Failed to load customer issue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (recno) {
      fetchIssue();
    }
  }, [recno]);

  const handleUpdateSuccess = () => {
    fetchIssue();
  };

  const handleBackClick = () => {
    history.push('/customer_issues/search');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackClick}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Customer Issues
        </Link>
        <Typography color="text.primary">Issue #{recno}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentLateIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
              Customer Issue Details
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Record #{recno}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Back to List
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {issue && !loading && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <CustomerIssueDetails issue={issue} showStatusChip={true} />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
              Update Issue
            </Typography>

            <CustomerIssueUpdateForm issue={issue} onSuccess={handleUpdateSuccess} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CustomerIssueDetailPage;
